import React, { useEffect, useMemo, useRef, useState } from 'react'
import axios from 'axios'
import moment from 'moment'
import { toast } from 'react-toastify'
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  useMediaQuery
} from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import CloseIcon from '@material-ui/icons/Close'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'

import api from 'repositories/api'

import { GoalSelectionList } from './GoalSelectionList'
import { BulkGoal, BulkGoalDeleteDialogProps, GoalGroupSection } from './types'
import { buildGoalGroupSections } from './utils'
import { useStyles } from './styles'

const months = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro'
]

const getErrorMessage = (error: unknown, fallback: string) => {
  const response = (error as {
    response?: { data?: { error?: { friendlyMsg?: string } } }
  })?.response

  return response?.data?.error?.friendlyMsg || fallback
}

export const BulkGoalDeleteDialog = ({
  groups,
  groupsError,
  groupsLoading,
  open,
  onClose,
  onDeleted,
  onReloadGroups
}: BulkGoalDeleteDialogProps) => {
  const classes = useStyles()
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('xs'))
  const loadRequestRef = useRef<ReturnType<typeof axios.CancelToken.source> | null>(null)
  const titleRef = useRef<HTMLHeadingElement>(null)
  const currentDate = moment()
  const [screen, setScreen] = useState<'selection' | 'confirmation'>('selection')
  const [selectedMonth, setSelectedMonth] = useState(currentDate.month() + 1)
  const [selectedYear, setSelectedYear] = useState(currentDate.year())
  const [loadedPeriod, setLoadedPeriod] = useState<string | null>(null)
  const [goals, setGoals] = useState<BulkGoal[]>([])
  const [selectedGoalIds, setSelectedGoalIds] = useState<string[]>([])
  const [hasLoaded, setHasLoaded] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    if (!open) return

    const now = moment()
    setScreen('selection')
    setSelectedMonth(now.month() + 1)
    setSelectedYear(now.year())
    setLoadedPeriod(null)
    setGoals([])
    setSelectedGoalIds([])
    setHasLoaded(false)
    setLoadError(null)
    setLoading(false)
    setDeleting(false)
  }, [open])

  useEffect(() => () => loadRequestRef.current?.cancel(), [])

  useEffect(() => {
    if (!open) return

    const animationFrame = window.requestAnimationFrame(() => titleRef.current?.focus())
    return () => window.cancelAnimationFrame(animationFrame)
  }, [open, screen])

  const selectedGoalIdSet = useMemo(
    () => new Set(selectedGoalIds),
    [selectedGoalIds]
  )
  const sections = useMemo(
    () => buildGoalGroupSections(goals, groups),
    [goals, groups]
  )
  const selectedSections = useMemo(
    () => sections
      .map(section => ({
        ...section,
        goals: section.goals.filter(goal => selectedGoalIdSet.has(goal._id))
      }))
      .filter(section => section.goals.length > 0),
    [sections, selectedGoalIdSet]
  )
  const selectedStoreCount = useMemo(
    () => new Set(selectedSections.flatMap(section => section.goals.map(goal => goal.store._id))).size,
    [selectedSections]
  )
  const periodIsValid = Number.isInteger(selectedYear) && selectedYear >= 1000 && selectedYear <= 9999
  const loadedPeriodLabel = loadedPeriod
    ? moment(`${loadedPeriod}-01`).format('MMMM [de] YYYY')
    : ''

  const resetResults = () => {
    setLoadedPeriod(null)
    setGoals([])
    setSelectedGoalIds([])
    setHasLoaded(false)
    setLoadError(null)
  }

  const handleClose = () => {
    if (deleting) return

    loadRequestRef.current?.cancel()
    loadRequestRef.current = null
    setLoading(false)
    onClose()
  }

  const loadGoals = async () => {
    if (!periodIsValid) return

    const period = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}`
    const request = axios.CancelToken.source()
    loadRequestRef.current = request
    setLoading(true)
    setLoadError(null)
    setSelectedGoalIds([])

    try {
      const { data } = await api.axios.get<BulkGoal[]>('/goal/bulk', {
        cancelToken: request.token,
        params: { month: period }
      })

      if (loadRequestRef.current !== request) return

      setGoals(data)
      setLoadedPeriod(period)
      setHasLoaded(true)
    } catch (error) {
      if (axios.isCancel(error) || loadRequestRef.current !== request) return

      setLoadError(getErrorMessage(
        error,
        'Não foi possível carregar as metas. Verifique sua conexão e tente novamente'
      ))
      setHasLoaded(false)
    } finally {
      if (loadRequestRef.current === request) {
        loadRequestRef.current = null
        setLoading(false)
      }
    }
  }

  const toggleGoal = (goalId: string) => {
    setSelectedGoalIds(current =>
      current.includes(goalId)
        ? current.filter(id => id !== goalId)
        : [...current, goalId]
    )
  }

  const toggleSection = (section: GoalGroupSection) => {
    const sectionGoalIds = section.goals.map(goal => goal._id)
    const sectionIsSelected = sectionGoalIds.every(id => selectedGoalIdSet.has(id))

    setSelectedGoalIds(current => {
      if (sectionIsSelected) {
        const sectionGoalIdSet = new Set(sectionGoalIds)
        return current.filter(id => !sectionGoalIdSet.has(id))
      }

      return Array.from(new Set([...current, ...sectionGoalIds]))
    })
  }

  const toggleAllGoals = () => {
    setSelectedGoalIds(
      selectedGoalIds.length === goals.length
        ? []
        : goals.map(goal => goal._id)
    )
  }

  const deleteGoals = async () => {
    setDeleting(true)
    try {
      const { data } = await api.axios.delete<{
        deletedCount: number
        requestedCount: number
      }>('/goal/bulk', { data: { goalIds: selectedGoalIds } })

      if (data.deletedCount === data.requestedCount) {
        toast.success(
          `${data.deletedCount} ${data.deletedCount === 1 ? 'meta excluída' : 'metas excluídas'} com sucesso.`
        )
      } else if (data.deletedCount === 0) {
        toast.error('Nenhuma meta foi excluída. Elas podem não estar mais disponíveis.')
      } else {
        toast.warn(
          `Exclusão parcial: ${data.deletedCount} de ${data.requestedCount} metas excluídas. As demais podem não estar mais disponíveis.`
        )
      }

      if (data.deletedCount > 0) onDeleted()
      onClose()
    } catch (error) {
      toast.error(getErrorMessage(
        error,
        'Não foi possível excluir as metas selecionadas.'
      ))
    } finally {
      setDeleting(false)
    }
  }

  const selectionTitle = 'Excluir metas'
  const confirmationTitle = 'Confirmar exclusão'

  return (
    <Dialog
      open={open}
      onClose={deleting ? undefined : handleClose}
      aria-describedby={screen === 'confirmation' ? 'bulk-goal-delete-warning' : undefined}
      aria-labelledby='bulk-goal-delete-title'
      classes={{ paper: classes.dialogPaper }}
      fullWidth
      fullScreen={fullScreen}
      maxWidth='md'
    >
      <DialogTitle className={classes.titleRoot} disableTypography>
        <Typography
          className={classes.title}
          component='h2'
          id='bulk-goal-delete-title'
          ref={titleRef}
          tabIndex={-1}
        >
          {screen === 'selection' ? selectionTitle : confirmationTitle}
        </Typography>
        <Typography className={classes.subtitle} variant='body2'>
          {screen === 'selection'
            ? 'Escolha o período e selecione as lojas cujas metas serão excluídas'
            : 'Revise o período e as lojas antes de concluir'}
        </Typography>
        <IconButton
          aria-label='Fechar exclusão de metas'
          className={classes.closeButton}
          disabled={deleting}
          onClick={handleClose}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.content} aria-busy={loading || deleting}>
        {screen === 'selection' ? (
          <>
            <div className={classes.periodPanel}>
              <Typography className={classes.sectionLabel}>Período das metas</Typography>
              <div className={classes.periodFields}>
                <TextField
                  disabled={loading}
                  fullWidth
                  label='Mês'
                  onChange={event => {
                    setSelectedMonth(Number(event.target.value))
                    resetResults()
                  }}
                  SelectProps={{
                    classes: { select: classes.monthSelect },
                    native: true
                  }}
                  select
                  value={selectedMonth}
                  variant='outlined'
                >
                  {months.map((month, index) => (
                    <option key={month} value={index + 1}>{month}</option>
                  ))}
                </TextField>
                <TextField
                  disabled={loading}
                  error={!periodIsValid}
                  fullWidth
                  helperText={!periodIsValid ? 'Informe um ano com 4 dígitos' : undefined}
                  inputProps={{ max: 9999, min: 1000, step: 1 }}
                  label='Ano'
                  onChange={event => {
                    setSelectedYear(Number(event.target.value))
                    resetResults()
                  }}
                  type='number'
                  value={selectedYear}
                  variant='outlined'
                />
              </div>
              <Typography className={classes.periodHint} variant='caption'>
                As lojas com meta nesse período aparecerão abaixo
              </Typography>
            </div>

            <div className={classes.resultArea}>
              {loading && (
                <div className={classes.statePanel} role='status'>
                  <CircularProgress className={classes.stateProgress} size={24} />
                  <Typography variant='body2'>
                    Carregando metas de {months[selectedMonth - 1].toLowerCase()} de {selectedYear}...
                  </Typography>
                </div>
              )}
              {!loading && loadError && (
                <div className={classes.statePanel} role='alert'>
                  <Typography className={classes.errorText} variant='body2'>
                    {loadError}
                  </Typography>
                </div>
              )}
              {!loading && !loadError && !hasLoaded && (
                <div className={classes.statePanel}>
                  <Typography variant='body2'>
                    Busque o período para consultar as metas disponíveis
                  </Typography>
                </div>
              )}
              {!loading && hasLoaded && goals.length === 0 && (
                <div className={classes.statePanel}>
                  <Typography className={classes.listTitle}>
                    Nenhuma meta em {loadedPeriodLabel}
                  </Typography>
                  <Typography variant='body2'>
                    Escolha outro período para continuar
                  </Typography>
                </div>
              )}
              {!loading && hasLoaded && goals.length > 0 && groupsLoading && (
                <div className={classes.statePanel} role='status'>
                  <CircularProgress className={classes.stateProgress} size={24} />
                  <Typography variant='body2'>Organizando lojas por regional...</Typography>
                </div>
              )}
              {!loading && hasLoaded && goals.length > 0 && !groupsLoading && groupsError && (
                <div className={classes.statePanel} role='alert'>
                  <Typography className={classes.errorText} variant='body2'>
                    Não foi possível organizar as lojas por regional
                  </Typography>
                  <Button
                    className={classes.utilityButton}
                    color='primary'
                    onClick={onReloadGroups}
                    size='small'
                  >
                    Tentar carregar regionais
                  </Button>
                </div>
              )}
              {!loading && hasLoaded && goals.length > 0 && !groupsLoading && !groupsError && (
                <GoalSelectionList
                  sections={sections}
                  selectedGoalIds={selectedGoalIdSet}
                  onToggleAll={toggleAllGoals}
                  onToggleGoal={toggleGoal}
                  onToggleSection={toggleSection}
                />
              )}
            </div>
          </>
        ) : (
          <div className={classes.confirmation}>
            <Typography className={classes.confirmationEyebrow}>
              Exclusão permanente
            </Typography>
            <Typography className={classes.confirmationTitle} component='p'>
              {selectedGoalIds.length === 1 ? 'A meta de' : 'As metas de'}{' '}
              {loadedPeriodLabel}{' '}
              {selectedGoalIds.length === 1
                ? 'será excluída da loja selecionada'
                : `serão excluídas de ${selectedStoreCount} lojas`}
            </Typography>

            <div className={classes.warningBox} id='bulk-goal-delete-warning'>
              <ErrorOutlineIcon className={classes.warningIcon} fontSize='small' />
              <div>
                <Typography className={classes.warningTitle} variant='body2'>
                  Esta ação é irreversível
                </Typography>
                <Typography className={classes.warningText} variant='body2'>
                  As metas selecionadas serão removidas permanentemente e não poderão ser recuperadas
                </Typography>
              </div>
            </div>

            <div className={classes.reviewList}>
              <Typography className={classes.sectionLabel}>Lojas selecionadas</Typography>
              {selectedSections.map(section => (
                <div className={classes.reviewGroup} key={section.id}>
                  <Typography className={classes.reviewGroupName}>
                    {section.name}
                  </Typography>
                  <Typography className={classes.reviewStoreNames} variant='body2'>
                    {section.goals.map(goal => goal.store.name).join(', ')}
                  </Typography>
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>

      <DialogActions
        className={`${classes.actions} ${
          screen === 'selection' ? classes.selectionActions : classes.confirmationActions
        }`}
      >
        {screen === 'selection' ? (
          <Button
            className={classes.primaryButton}
            color='primary'
            disabled={loading || (!hasLoaded && !periodIsValid) || (hasLoaded && (groupsLoading || groupsError || selectedGoalIds.length === 0))}
            onClick={hasLoaded ? () => setScreen('confirmation') : loadGoals}
            variant='contained'
          >
            {loading && (
              <CircularProgress
                className={classes.buttonProgress}
                color='inherit'
                size={18}
                aria-hidden='true'
              />
            )}
            {loading
              ? 'Carregando...'
              : hasLoaded
                ? `Revisar exclusão${selectedGoalIds.length > 0 ? ` (${selectedGoalIds.length})` : ''}`
                : loadError
                  ? 'Tentar novamente'
                  : 'Buscar metas'}
          </Button>
        ) : (
          <>
            <Button disabled={deleting} onClick={() => setScreen('selection')}>
              Voltar
            </Button>
            <Button
              className={classes.destructiveButton}
              disabled={deleting}
              onClick={deleteGoals}
              variant='contained'
            >
              {deleting && (
                <CircularProgress
                  className={classes.buttonProgress}
                  color='inherit'
                  size={18}
                  aria-hidden='true'
                />
              )}
              {deleting
                ? 'Excluindo...'
                : `Excluir ${selectedGoalIds.length} ${selectedGoalIds.length === 1 ? 'meta' : 'metas'}`}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}
